<script setup lang="ts">
import { useLocationResolver } from "@/composable/location-resolver";
import { mdiArchiveArrowDown, mdiBellAlertOutline, mdiCogOutline } from "@mdi/js";
import { Accordion, AccordionItem, Divider, Icon, LinkButton } from "@soft-db/ui";
import { icLineAxis } from "../icons";

const { t } = useI18n();
const route = useRoute();

const { resolve } = useLocationResolver();

const settingsTab = computed(() => {
	const params = { id: route.params.id || "" };
	return {
		icon: mdiCogOutline,
		title: t("common.settings"),
		to: resolve({ name: "sites-id-settings", params, query: route.query }),
	};
});

const limitsTab = computed(() => {
	const params = { id: route.params.id || "" };
	return {
		icon: icLineAxis,
		title: t("project-settings.limits"),
		to: resolve({ name: "sites-id-limits", params, query: route.query }),
	};
});

const alertsTab = computed(() => {
	const params = { id: route.params.id || "" };
	return {
		icon: mdiBellAlertOutline,
		title: t("object.alert", 2),
		to: resolve({ name: "sites-id-alerts-index", params, query: route.query }),
	};
});

const downloadTab = computed(() => {
	const params = { id: route.params.id || "" };
	return {
		icon: mdiArchiveArrowDown,
		title: t("project-settings.download"),
		to: resolve({ name: "sites-id-archive", params, query: route.query }),
	};
});

const tabs = computed(() => {
	return [settingsTab.value, limitsTab.value, alertsTab.value, downloadTab.value];
});

const currentTab = computed(() => {
	if (route.path.includes("limits")) {
		return limitsTab.value;
	} else if (route.path.includes("alerts")) {
		return alertsTab.value;
	} else if (route.path.includes("archive")) {
		return downloadTab.value;
	} else {
		return settingsTab.value;
	}
});

const openTab = ref(false);
</script>

<template>
	<div class="md:@container">
		<Accordion
			class="bg-base-200 border-border @lg:hidden w-full rounded-3xl border pl-2 pr-4"
			type="multiple"
		>
			<AccordionItem :defaultOpen="openTab">
				<template #trigger>
					<button class="flex items-center gap-2 px-2" @click="() => (openTab = !openTab)">
						<Icon :d="currentTab.icon" />
						<h3>{{ currentTab.title }}</h3>
					</button>
				</template>
				<Divider class="my-0!" />

				<template v-for="tab of tabs" :key="tab.title">
					<LinkButton
						v-if="tab.to"
						:to="tab.to"
						class="flex justify-start"
						activeClass="text-primary border"
					>
						<Icon size="sm" :d="tab.icon" />
						<h5>{{ tab.title }}</h5>
					</LinkButton>
				</template>
			</AccordionItem>
		</Accordion>
		<div class="border-border z-2 @lg:flex hidden gap-1 rounded-2xl border">
			<template v-for="tab of tabs" :key="tab.title">
				<LinkButton
					v-if="tab.to"
					:to="tab.to"
					class="flex-center bg-base-300 border-base-300 hover:bg-focus flex-1 rounded-2xl"
					activeClass="border-primary text-primary border"
				>
					<Icon size="sm" :d="tab.icon" />
					<h5>{{ tab.title }}</h5>
				</LinkButton>
			</template>
		</div>
	</div>
</template>
